/** BRL centavos as "R$ 49,50"; whole amounts drop the cents. */
export function formatBrl(cents: number, locale: string): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** "14/12" in pt-BR, "12/14" in en: day and month only. */
export function formatDayMonth(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "2-digit" });
}

export function formatFullDate(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatDateTime(iso: string, locale: string): string {
  return new Date(iso).toLocaleString(locale, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Opens a hosted page: the desktop shell hands it to the system browser, the web navigates. */
export function openHostedPage(url: string): void {
  // A new tab keeps the app open; a blocked popup falls back to leaving this tab.
  const tab = window.open(url, "_blank");
  if (tab) tab.opener = null;
  else window.location.assign(url);
}

/** Keeps digits only, for CPF and phone inputs. */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

/** Formats up to 11 digits as 000.000.000-00 while typing. */
export function formatCpf(value: string): string {
  return digitsOnly(value)
    .slice(0, 11)
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
}

/** The check digits of a CPF, so a typo is caught before the request. */
export function isValidCpf(value: string): boolean {
  const d = digitsOnly(value);
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false;
  const digit = (len: number) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(d[i]) * (len + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return digit(9) === Number(d[9]) && digit(10) === Number(d[10]);
}

/** A Brazilian number typed with or without the country code, as E.164. */
export function toE164Brazil(value: string): string | null {
  let d = digitsOnly(value);
  if (d.startsWith("55") && d.length >= 12) d = d.slice(2);
  return d.length === 11 || d.length === 10 ? `+55${d}` : null;
}
